import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUserPlus, FaEllipsisV, FaSearch, FaEdit, FaLock, FaLockOpen } from 'react-icons/fa';
import Button from '../../components/Button';
import Table from '../../components/Table';
import Select from '../../components/Select';
import Skeleton from '../../components/Skeleton';
import PageHeader from '../../components/PageHeader';
import ProgressBar from '../../components/ProgressBar';
import ModalCrearUsuario from './components/ModalCrearUsuario';
import PanelDetalleUsuario from './components/PanelDetalleUsuario';
import { adminUserService } from '../../services/adminUserService';
import { planService } from '../../services/planService';
import { formatDate } from '../../utils/helpers';

const AVATAR_COLORS = ['#3B82F6', '#22C55E', '#A78BFA', '#F97316', '#EC4899'];

const Avatar = ({ fullName }) => {
  const parts = (fullName || '?').trim().split(' ');
  const initials = parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : (parts[0][0] || '?').toUpperCase();
  const color = AVATAR_COLORS[(fullName?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%',
      background: `linear-gradient(135deg, ${color}, ${color}99)`,
      boxShadow: `0 0 8px ${color}40`,
      color: '#fff',
      fontSize: 11,
      fontWeight: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      {initials}
    </div>
  );
};

const UsageCell = ({ row, type }) => {
  if (row.is_blocked) {
    return (
      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, backgroundColor: 'rgba(220,31,38,0.12)', color: '#F87171', border: '1px solid rgba(220,31,38,0.3)' }}>
        Bloqueado
      </span>
    );
  }
  const used = type === 'text' ? (row.text_used ?? 0) : (row.voice_used ?? 0);
  const limit = type === 'text' ? (row.text_limit ?? 0) : (row.voice_limit ?? 0);
  return (
    <div className="space-y-1 min-w-[110px]">
      <ProgressBar value={used} max={limit} height={5} />
      <span style={{ fontSize: 11, color: '#6B7391' }}>{(used).toLocaleString()} / {(limit).toLocaleString()}</span>
    </div>
  );
};

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'active', label: 'Activos' },
  { value: 'blocked', label: 'Bloqueados' },
];

const AdminUsuariosPage = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [users, plansData] = await Promise.all([
        adminUserService.getAll(),
        planService.getAll(),
      ]);
      setAllUsers(users);
      setPlans(plansData);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('[data-dropdown]')) setOpenDropdown(null);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const filteredUsers = allUsers.filter(u => {
    if (filterStatus === 'active' && u.is_blocked) return false;
    if (filterStatus === 'blocked' && !u.is_blocked) return false;
    if (filterPlan && u.plan_name !== filterPlan) return false;
    if (search) {
      const q = search.toLowerCase();
      return (u.full_name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.whatsapp_number || '').toLowerCase().includes(q);
    }
    return true;
  });

  const planFilterOptions = [
    { value: '', label: 'Todos los planes' },
    ...plans.map(p => ({ value: p.name, label: p.name })),
  ];

  const columns = [
    {
      key: 'full_name', title: 'Usuario', sortable: true,
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Avatar fullName={row.full_name} />
          <div>
            <p style={{ fontSize: 13, fontWeight: 500, color: '#E8EAF0' }}>{row.full_name || '—'}</p>
            <p style={{ fontSize: 11, color: '#6B7391' }}>{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'plan_name', title: 'Plan', sortable: true,
      render: (v) => (
        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, backgroundColor: 'rgba(59,130,246,0.12)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.3)' }}>
          {v || '—'}
        </span>
      ),
    },
    {
      key: 'is_blocked', title: 'Estado', sortable: true,
      render: (v) => v
        ? <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 500, color: '#F87171' }}><FaLock size={10} /> Bloqueado</span>
        : <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 500, color: '#4ADE80' }}><FaLockOpen size={10} /> Activo</span>,
    },
    {
      key: 'text_used', title: 'Texto/sem', sortable: false,
      render: (_, row) => <UsageCell row={row} type="text" />,
    },
    {
      key: 'voice_used', title: 'Voz/sem', sortable: false,
      render: (_, row) => <UsageCell row={row} type="voice" />,
    },
    {
      key: 'registered_at', title: 'Registro', sortable: true,
      render: (v) => <span style={{ fontSize: 11, color: '#6B7391' }}>{v ? formatDate(v) : '—'}</span>,
    },
    {
      key: 'actions', title: '', sortable: false,
      render: (_, row) => (
        <div className="relative" data-dropdown>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setOpenDropdown(prev => prev === row.user_id ? null : row.user_id)}
            className="p-2 rounded-full hover:bg-white/5 transition-colors"
            style={{ color: '#6B7391' }}
          >
            <FaEllipsisV size={14} />
          </motion.button>
          <AnimatePresence>
            {openDropdown === row.user_id && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -4 }}
                transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                className="absolute right-0 top-8 z-20 w-44 rounded-xl py-1"
                style={{ backgroundColor: '#1a1b30', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 12px 32px rgba(0,0,0,0.6)', transformOrigin: 'top right' }}
              >
                <button
                  onClick={() => { setSelectedUser(row); setOpenDropdown(null); }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors"
                  style={{ color: '#9BA3B8' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <FaEdit size={12} style={{ color: '#3B82F6' }} />
                  Ver / Editar
                </button>
                {row.is_blocked ? (
                  <button
                    onClick={() => { setSelectedUser({ ...row, _goToTab: 'Bloqueo' }); setOpenDropdown(null); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-green-400 hover:bg-white/5"
                  >
                    <FaLockOpen size={12} /> Desbloquear
                  </button>
                ) : (
                  <button
                    onClick={() => { setSelectedUser({ ...row, _goToTab: 'Bloqueo' }); setOpenDropdown(null); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-white/5"
                  >
                    <FaLock size={12} /> Bloquear
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Administrar Usuarios" subtitle="Gestiona planes, límites y acceso de usuarios">
        <Button variant="warning" onClick={() => setShowCreateModal(true)} leftIcon={<FaUserPlus size={13} />}>
          Crear usuario
        </Button>
      </PageHeader>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        style={{
          background: 'linear-gradient(160deg, #161731 0%, #12132a 55%, #0f1024 100%)',
          border: '1px solid rgba(255,255,255,0.07)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
          borderRadius: '12px',
          padding: '24px',
        }}
      >
        {/* Filtros */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex flex-wrap gap-3 flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2" size={13} style={{ color: '#6B7391' }} />
              <input
                type="text"
                placeholder="Buscar por nombre, email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm rounded-lg w-72 focus:outline-none"
                style={{
                  backgroundColor: '#1a1b30',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#E8EAF0',
                  caretColor: '#00C2FF',
                  transition: 'border-color .2s, box-shadow .2s',
                }}
                onFocus={e => { e.target.style.borderColor = 'rgba(0,194,255,0.4)'; e.target.style.boxShadow = '0 0 0 2px rgba(0,194,255,0.08)'; }}
                onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
              />
            </div>
            <div className="w-44">
              <Select options={planFilterOptions} value={filterPlan} onChange={e => setFilterPlan(e.target.value)} placeholder="Plan..." isClearable={false} isSearchable={false} />
            </div>
            <div className="w-44">
              <Select options={STATUS_OPTIONS} value={filterStatus} onChange={e => setFilterStatus(e.target.value)} placeholder="Estado..." isClearable={false} isSearchable={false} />
            </div>
          </div>
        </div>

        {loading ? (
          <Skeleton.TableRows rows={8} cols={7} />
        ) : (
          <Table
            data={filteredUsers}
            columns={columns}
            options={{
              pagination: true,
              pageSize: 20,
              pageSizeOptions: [10, 20, 50],
              showSearch: false,
              emptyMessage: 'No se encontraron usuarios',
            }}
          />
        )}
      </motion.div>

      <ModalCrearUsuario
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={fetchData}
        plans={plans}
      />

      <AnimatePresence>
        {selectedUser && (
          <PanelDetalleUsuario
            user={selectedUser}
            plans={plans}
            onClose={() => setSelectedUser(null)}
            onUpdated={() => { fetchData(); setSelectedUser(null); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminUsuariosPage;
